import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import { environment } from '../../../environments/environment';
import {
  AuthErrorResponse,
  AuthSuccessResponse,
  CompleteProfilePayload,
  DevUser,
  Profile,
  ProfileUpdatePayload,
  RegisterPayload,
  TwoFactorSetup,
  User,
} from './auth.types';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly STORAGE_KEY = 'auth_user';

  readonly currentUser = signal<User | null>(this.loadFromStorage());
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly profileCompleted = computed(() => this.currentUser()?.profile_completed_at != null);
  /**
   * True while the signed-in user's standing (sign-up) consent to AI-assisted
   * assessment stands. The application screens hide their consent box on it and
   * the server derives each application's consent from it — so no screen needs
   * to ask again unless it is absent or withdrawn.
   */
  readonly hasStandingAiConsent = computed(
    () => this.currentUser()?.ai_processing_consent === true,
  );

  private loadFromStorage(): User | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }

  setUser(user: User): void {
    this.currentUser.set(user);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
    Sentry.setUser({ id: user.id }); // email omitted: id alone links events (POPIA).
  }

  clearUser(): void {
    this.currentUser.set(null);
    localStorage.removeItem(this.STORAGE_KEY);
    Sentry.setUser(null);
  }

  login(email: string, password: string, totpCode?: string): Observable<User> {
    const body: { email: string; password: string; totp_code?: string } = { email, password };
    if (totpCode) body.totp_code = totpCode;
    return this.http
      .post<User>(`${environment.apiUrl}/api/auth/login/`, body)
      .pipe(tap((user) => this.setUser(user)));
  }

  register(data: RegisterPayload): Observable<{ code: string; detail: string }> {
    return this.http.post<{ code: string; detail: string }>(
      `${environment.apiUrl}/api/auth/register/`,
      data,
    );
  }

  verifyEmail(token: string): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/api/auth/verify-email/`, { token })
      .pipe(tap((user) => this.setUser(user)));
  }

  resendVerification(email: string): Observable<void> {
    return this.http
      .post<unknown>(`${environment.apiUrl}/api/auth/resend-verification/`, { email })
      .pipe(map(() => undefined));
  }

  completeProfile(data: CompleteProfilePayload): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/api/me/profile/complete/`, data)
      .pipe(tap((user) => this.setUser(user)));
  }

  /** Read the signed-in user's editable profile. */
  getProfile(): Observable<Profile> {
    return this.http.get<Profile>(`${environment.apiUrl}/api/me/profile/`);
  }

  /**
   * Update the signed-in user's profile. Keeps the cached {@link currentUser}
   * in step so the header greeting reflects a name change immediately.
   */
  updateProfile(data: ProfileUpdatePayload): Observable<Profile> {
    return this.http
      .patch<Profile>(`${environment.apiUrl}/api/me/profile/`, data)
      .pipe(tap((p) => this.mergeIntoCurrentUser(p)));
  }

  /** Begin TOTP enrolment — returns the secret + otpauth URI to render as a QR. */
  startTwoFactorSetup(): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>(`${environment.apiUrl}/api/me/2fa/setup/`, {});
  }

  /** Confirm enrolment with the first code; flips 2FA on for the account. */
  confirmTwoFactor(code: string): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/api/me/2fa/confirm/`, { code })
      .pipe(tap((user) => this.setUser(user)));
  }

  /** Turn 2FA off, re-proving identity with a code or the account password. */
  disableTwoFactor(proof: { code?: string; password?: string }): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/api/me/2fa/disable/`, proof)
      .pipe(tap((user) => this.setUser(user)));
  }

  /**
   * Record the standing AI-processing consent from the profile page — the
   * sign-up question asked late (an account from before the capture, or
   * consenting again after a withdrawal). Answers with the refreshed profile.
   */
  giveAiConsent(): Observable<Profile> {
    return this.http
      .post<Profile>(`${environment.apiUrl}/api/me/ai-consent/`, {
        ai_processing_consent: true,
      })
      .pipe(tap(() => this.setAiConsentFlag(true)));
  }

  /**
   * Withdraw the standing AI-processing consent (POPIA s11(2)). The server
   * cascades it to every live per-application consent this person gave, so
   * every AI gate shuts at once; the cached user is flipped so the application
   * screens put the question again.
   */
  withdrawAiConsent(): Observable<Profile> {
    return this.http
      .post<Profile>(`${environment.apiUrl}/api/me/ai-consent/withdraw/`, {})
      .pipe(tap(() => this.setAiConsentFlag(false)));
  }

  /**
   * Note that the standing consent now stands, after an application screen's
   * fallback box was ticked: the server records the standing row from that
   * tick, and the cached user must say so or the next screen asks again.
   */
  markAiConsentGiven(): void {
    this.setAiConsentFlag(true);
  }

  private setAiConsentFlag(value: boolean): void {
    const current = this.currentUser();
    if (!current || current.ai_processing_consent === value) return;
    this.setUser({ ...current, ai_processing_consent: value });
  }

  /** Patch the cached user with freshly-saved profile fields, persisting it. */
  private mergeIntoCurrentUser(p: Profile): void {
    const current = this.currentUser();
    if (!current) return;
    this.setUser({
      ...current,
      first_name: p.first_name,
      last_name: p.last_name,
      two_factor_enabled: p.two_factor_enabled,
    });
  }

  logout(): Observable<void> {
    return this.http.post<unknown>(`${environment.apiUrl}/api/auth/logout/`, {}).pipe(
      map(() => undefined),
      tap(() => {
        this.clearUser();
        void this.router.navigate(['/auth/login']);
      }),
      catchError(() => {
        this.clearUser();
        void this.router.navigate(['/auth/login']);
        return of(undefined);
      }),
    );
  }

  requestPasswordReset(email: string): Observable<AuthSuccessResponse> {
    return this.http.post<AuthSuccessResponse>(
      `${environment.apiUrl}/api/auth/password-reset/request/`,
      { email },
    );
  }

  confirmPasswordReset(token: string, newPassword: string): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/api/auth/password-reset/confirm/`, {
        token,
        new_password: newPassword,
      })
      .pipe(tap((user) => this.setUser(user)));
  }

  /**
   * Dev-only: list the seeded demo users for the Debug "Switch User" menu.
   *
   * Gated on `debug`, NOT on `production`: staging is a production *build* that
   * deliberately keeps the Debug menu (`debug: true`, matching the backend's
   * `ALLOW_DEV_SWITCH`). Keying this on `production` short-circuited staging to
   * an empty list without ever making the request, so the menu rendered and then
   * reported "No users found" no matter what was seeded.
   *
   * Real production sets `debug: false`, so the request is still skipped there —
   * the backend route 404s anyway.
   *
   * Failures are NOT swallowed. An empty roster and an unreachable endpoint need
   * different messages ("nothing seeded" vs "the dev routes are off"), and a
   * `catchError` to `[]` here made them indistinguishable to every caller.
   */
  devListUsers(): Observable<DevUser[]> {
    if (!environment.debug) return of([]);
    return this.http
      .get<{ users: DevUser[] }>(`${environment.apiUrl}/api/auth/dev/users/`)
      .pipe(map((res) => res.users));
  }

  /**
   * Dev-only: log in as a seeded demo user without a password. Sets the active
   * user on success, exactly like {@link login}.
   */
  devSwitchUser(email: string): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/api/auth/dev/switch-user/`, { email })
      .pipe(tap((user) => this.setUser(user)));
  }

  refreshToken(): Observable<void> {
    return this.http
      .post<unknown>(`${environment.apiUrl}/api/auth/token/refresh/`, {})
      .pipe(map(() => undefined));
  }

  loadCurrentUser(): Observable<User | null> {
    const storedUser = this.loadFromStorage();
    if (!storedUser) return of(null);
    return this.refreshToken().pipe(
      map(() => storedUser),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.clearUser();
        }
        return of(null);
      }),
    );
  }

  getAuthError(err: HttpErrorResponse): AuthErrorResponse | null {
    if (err.error && typeof err.error === 'object' && 'code' in err.error) {
      return err.error as AuthErrorResponse;
    }
    return null;
  }
}
